import { GraphqlClient } from 'src/api/graphql_client';
import {
  MemberRepository,
  MembersQueryParams,
  InvitesQueryParams,
  Users,
  Invitations,
} from 'src/api/member_repository';
import {
  InvitationFieldsFragment,
  InviteUserDocument,
  InviteUserMutation,
  InviteUserMutationVariables,
  GetInvitationsDocument,
  GetInvitationsQuery,
  GetInvitationsQueryVariables,
  GetMembersDocument,
  GetMembersQuery,
  GetMembersQueryVariables,
  RemoveInvitationDocument,
  RemoveInvitationMutation,
  RemoveInvitationMutationVariables,
  ReplaceRolesDocument,
  ReplaceRolesMutation,
  ReplaceRolesMutationVariables,
  RemoveUsersDocument,
  RemoveUsersMutation,
  RemoveUsersMutationVariables,
  RoleFieldsFragment,
  GetAssignableRolesQuery,
  GetAssignableRolesDocument,
} from 'src/types';
import {
  InvitationsError,
  InvitationErrorType,
  InvitationErrorDetails,
  GraphqlError,
} from 'src/types/errors';
import { InvitationInput } from 'src/types/member_management';

/**
 * Base class for the implementation of the Member Repository for a GraphQL backend.
 */
export class GraphqlMemberRepository implements MemberRepository {
  constructor(private readonly _graphqlClient: GraphqlClient) {}

  async getAssignableRoles(): Promise<RoleFieldsFragment[]> {
    const data = await this._graphqlClient.request<GetAssignableRolesQuery>(
      GetAssignableRolesDocument,
    );

    return data.auth0.assignableRoles;
  }

  async getMembers(membersQueryParams: MembersQueryParams): Promise<Users> {
    const pageIndex = Math.max(0, (membersQueryParams.page || 1) - 1);
    const variables: GetMembersQueryVariables = {
      page: pageIndex,
      perPage: membersQueryParams.perPage || 15,
    };

    const response = await this._graphqlClient.request<GetMembersQuery>(
      GetMembersDocument,
      variables,
    );

    return response.auth0.users;
  }

  async getInvites(
    invitesQueryParams: InvitesQueryParams,
  ): Promise<Invitations> {
    const pageIndex = Math.max(0, (invitesQueryParams.page || 1) - 1);
    const variables: GetInvitationsQueryVariables = {
      page: pageIndex,
      perPage: invitesQueryParams.perPage || 20,
    };

    const response = await this._graphqlClient.request<GetInvitationsQuery>(
      GetInvitationsDocument,
      variables,
    );

    return response.auth0.invitations;
  }

  async inviteUsers(
    invites: InvitationInput[],
  ): Promise<Array<InvitationFieldsFragment | null>> {
    const results: Array<InvitationFieldsFragment | null> = [];
    const errors: InvitationErrorDetails[] = [];
    const successfulEmails: string[] = [];

    // Process each invitation individually to capture specific errors
    for (const invite of invites) {
      try {
        const variables: InviteUserMutationVariables = {
          email: invite.email,
          roleIds: invite.roleIds,
        };

        const response = await this._graphqlClient.request<InviteUserMutation>(
          InviteUserDocument,
          variables,
        );

        results.push(response.inviteUser!.invitation);
        successfulEmails.push(invite.email);
      } catch (error) {
        if (error instanceof GraphqlError) {
          const errorType = this.parseInvitationError(
            (error as GraphqlError).code,
          );
          errors.push({
            email: invite.email,
            type: errorType,
            message: InvitationsError.getErrorMessage(errorType, invite.email),
          });
        } else {
          errors.push({
            email: invite.email,
            type: InvitationErrorType.UNKNOWN_ERROR,
            message: InvitationsError.getErrorMessage(
              InvitationErrorType.UNKNOWN_ERROR,
              invite.email,
            ),
          });
        }
        results.push(null);
      }
    }

    // If there are any errors, throw InvitationsError for special handling
    if (errors.length > 0) {
      throw new InvitationsError(errors, successfulEmails);
    }

    return results;
  }

  private parseInvitationError(errorCode: number): InvitationErrorType {
    if (errorCode === 40000201) {
      return InvitationErrorType.ALREADY_INVITED;
    } else if (errorCode === 40000202) {
      return InvitationErrorType.ALREADY_MEMBER;
    }

    return InvitationErrorType.UNKNOWN_ERROR;
  }

  async assignRole(
    memberId: string,
    roleIds: string[],
    _organizationId: string,
  ): Promise<void> {
    const variables: ReplaceRolesMutationVariables = {
      userId: memberId,
      roleIds,
    };

    await this._graphqlClient.request<ReplaceRolesMutation>(
      ReplaceRolesDocument,
      variables,
    );
  }

  async removeUser(memberId: string, _organizationId: string): Promise<void> {
    const variables: RemoveUsersMutationVariables = {
      userIds: [memberId],
    };

    await this._graphqlClient.request<RemoveUsersMutation>(
      RemoveUsersDocument,
      variables,
    );

    return Promise.resolve();
  }

  async removeInvite(inviteId: string, _organizationId: string): Promise<void> {
    const variables: RemoveInvitationMutationVariables = {
      inviteId,
    };

    await this._graphqlClient.request<RemoveInvitationMutation>(
      RemoveInvitationDocument,
      variables,
    );

    return Promise.resolve();
  }
}
